import { z } from 'zod';
import {
  OrderSchema,
  orderAllocationSchema,
  orderDestinationSchema,
  orderDropoffExpectationSourceSchema,
  latLngSchema,
  Order,
  orderBeneficiarySchema,
  orderContactSchema,
  orderDestinationContactSchema,
  idSchema,
  objectIdSchema,
  OrderActionTypesSchema,
  amountSchema,
  priceSchema,
  orderImageSchema,
  vehicleTypesSchema,
  Vehicle,
  orderOverallStatusSchema,
  orderDestinationStatusSchema,
  orderDestinationTypeSchema,
  dateTypeSchema,
  booleanSchema,
  orderPODCopyStatusSchema,
  orderPODImageStatusSchema,
  currencySchema,
  feeSchema,
  paymentMethodSchema,
  orderAvailableMessagesSchema,
  orderCancellationSchema,
  dateSchema,
  orderReviewStatusSchema,
  searchColSchema,
  orderPODCopySettingSchema,
  type Fee,
  type TaxId,
} from '@hero/schema';
import { paginationSchema } from '@hero/schema';
import { API, Group } from '../types';

export const orderUnifiedStatusSchema = z
  .enum([
    ...orderOverallStatusSchema.options,
    'accepted',
    'published',
    'pending',
    'not_assigned',
    'any_active_status',
    'active_after_confirmed',
    'success_orders',
    'verified_orders',
    'unverified_orders',
  ])
  .describe(
    "Unified order-status filter that bundles raw `OrderOverallStatus` values with virtual filter groups. Virtual values: 'accepted' (platform-accepted, not yet published) | 'published' (open on the load board) | 'pending' (new + accepted, awaiting confirmation) | 'not_assigned' (Order.driver party absent, order still active — awaiting a driver pick) | 'any_active_status' (new / confirmed / in_transit_pickup / arrived_pickup / in_transit_dropoff / arrived_dropoff) | 'active_after_confirmed' (same minus new + accepted) | 'success_orders' (everything except canceled) | 'verified_orders' / 'unverified_orders' (delivered orders by admin verification state).",
  );

/**
 * Which date field on the order to filter by.
 */
export const orderDateBySchema = z
  .enum(['pickup', 'created', 'delivered', 'canceled'])
  .describe(
    "Which `Order` date field the `simpleDate` window applies to. 'pickup' → `dates.pickup.from`; 'created' → `doc.createdAt`; 'delivered' → `dates.deliveredAt`; 'canceled' → `cancellation.date`. Defaults to 'created' when omitted.",
  );
export type OrderDateBy = z.infer<typeof orderDateBySchema>;

export const ordersQueryAPISchema = z.object({
  search: searchColSchema.describe(
    'Atlas Search full-text query; when set, results are filtered by this term across searchable order fields (refs, beneficiary names, city names, plate, items).',
  ),

  unifiedStatus: orderUnifiedStatusSchema
    .optional()
    .describe(
      'Filter by unified order status (combines raw statuses + virtual groups). See `orderUnifiedStatusSchema` for the value list.',
    ),

  pickupDate: dateSchema
    .optional()
    .describe('Filter to orders whose pickup date (`dates.pickup.from`) matches this exact day.'),
  pickupDateBeforeOrEqual: dateSchema
    .optional()
    .describe(
      'Filter to orders whose pickup date is on or before this date. Use to find overdue / past pickups.',
    ),

  pickupSimpleDate: dateTypeSchema
    .optional()
    .describe(
      "Simple pickup-date window: 'today', 'this_week', 'this_month', 'this_year', 'last_week', 'last_month', etc. Filters `dates.pickup.from` to that range.",
    ),

  deliveryDate: dateSchema
    .optional()
    .describe('Filter to orders delivered on this exact day (`dates.deliveredAt`).'),

  simpleDate: dateTypeSchema
    .optional()
    .describe(
      "Generic simple-date filter (today, this_week, this_month, etc.). Used together with `dateBy` to pick which date field. When set without `dateBy`, defaults to filtering by creation date ('created').",
    ),

  dateBy: orderDateBySchema
    .optional()
    .describe(
      "Which order date field `simpleDate` targets. Only used when `simpleDate` is set. Defaults to 'created'.",
    ),

  pickupDestination: objectIdSchema
    .optional()
    .describe(
      "`Region._id` (city / hub) of the order's pickup destination. Resolve from `search_regions` when the user names a city.",
    ),

  orderRef: z.coerce
    .string()
    .optional()
    .describe(
      "Order reference (main or short, e.g. 'ORD-2026-00042' or 'O42'). Folded into the search predicate server-side.",
    ),

  waybillNumber: z.coerce
    .string()
    .optional()
    .describe(
      "Government e-waybill number (وثيقة النقل / Bayan) as printed on the document — matches `waybillBayan.tripId` exactly. This is the only identifier a Wathiqa Plus user has: they never see a Hero order ref, so 'close waybill 284475' resolves through here.",
    ),

  podStatus: orderPODCopyStatusSchema
    .optional()
    .describe('Filter orders by their POD copy status (paper-original-POD lifecycle).'),

  podImageStatus: z
    .union([orderPODImageStatusSchema, z.literal('active_pod_images')])
    .optional()
    .describe(
      "Filter orders by POD image status. Virtual value 'active_pod_images' matches any order POD that is still ongoing (not 'not_required' / not complete).",
    ),

  dropOffDestination: objectIdSchema
    .optional()
    .describe(
      "`Region._id` (city / hub) of the order's final drop-off destination. Resolve from `search_regions` when the user names a city.",
    ),
  itemsCostsType: z
    .enum(['high', 'medium', 'low', 'none'])
    .optional()
    .describe(
      "Bucket filter for the declared items value on the load. 'high' = 100k+, 'medium' = 50k+, 'low' = 10k+, 'none' = 0.",
    ),
  carrierId: objectIdSchema
    .optional()
    .describe(
      'Filter to orders allocated to a specific carrier Org (`Carrier.org._id`). Mostly admin-side; tenant scope already restricts visibility.',
    ),
  customerId: objectIdSchema
    .optional()
    .describe(
      'Filter to orders associated with a specific business / individual customer Org id. NOTE: server-side this remaps onto `shipper._id` for Phase-7 docs.',
    ),
  driverId: objectIdSchema
    .optional()
    .describe(
      'Filter to orders currently assigned to a specific Driver doc (`Driver._id` → `Order.driver._id`). Used by the driver-drawer orders tab and agent tools; AND-narrowed with tenant scope on `getMyOrders`. Resolve from `search_drivers` when the caller references a driver by name.',
    ),
  vehicleId: objectIdSchema
    .optional()
    .describe(
      'Filter to orders currently assigned to a specific Vehicle (`Vehicle._id`). Resolve from `search_vehicles({ plate })` when the caller references a vehicle by plate.',
    ),
  invoiceId: objectIdSchema
    .optional()
    .describe('Filter to orders that appear on a specific Invoice (`Invoice._id`).'),
  transactionId: objectIdSchema
    .optional()
    .describe('Filter to orders that appear on a specific Transaction (`Transaction._id`).'),
  vehicleTypes: vehicleTypesSchema
    .optional()
    .describe(
      "Filter orders requesting a specific vehicle type (e.g. 'truck_chilled'). NOTE: despite the plural name, this is a single enum value today. Use `vehicleTypesSchema` for the value list.",
    ),

  isSaaS: booleanSchema
    .optional()
    .describe(
      'Filter orders by SaaS flag. `true` = order is purely SaaS (no Hero involvement); `false` = Hero-managed.',
    ),

  thirdPartyCustomerId: objectIdSchema
    .optional()
    .describe(
      "Filter orders by the third-party customer (the business customer's own customer).",
    ),

  savedLocationId: objectIdSchema
    .optional()
    .describe(
      'Filter orders that reference this `SavedLocation._id` on any destination. Resolve from `search_saved_locations` when the user names a saved place.',
    ),

  orderIds: z
    .array(objectIdSchema)
    .optional()
    .describe('Filter to a specific set of order ids. Use for batch lookups.'),
});
export type OrdersQueryAPI = z.infer<typeof ordersQueryAPISchema>;
export type OrderUnifiedStatus = z.infer<typeof orderUnifiedStatusSchema>;
const destinationSchema = orderDestinationSchema
  .pick({
    type: true,
    instructions: true,
    isAccurateLocation: true,
    locationUrl: true,
    addressDetail: true,
  })
  .extend({
    location: latLngSchema,

    /** If the user choses from the saved locations, then we will use the saved location id.
     * if provided, then we will use the saved location instead of the location. */
    savedLocationId: objectIdSchema.optional(),

    /** the customer id coming from `Customer Collection`. */
    customerId: objectIdSchema.optional(),
    /**
     * If the user choses from the contacts, then we will use the contact ids.
     *
     * If not provided and there is a saved location, then we will use the contacts from the saved location.
     */
    contacts: z
      .array(orderDestinationContactSchema.pick({ _id: true, phone: true, name: true }))
      .optional(),
  })
  .partial({ isAccurateLocation: true });

const loadSchema = OrderSchema.data.shape.load;

// get one
export const orderGetOneAPISchema = z.object({
  id: objectIdSchema.describe(
    "`Order._id` (24-char Mongo ObjectId). NOTE: this is the only order route using `id` instead of `_id` for legacy reasons.",
  ),
});
export type OrderGetOneAPI = API<z.infer<typeof orderGetOneAPISchema>, Order>;

//* new order
export const createOrderAPISchema = loadSchema
  .pick({
    vehicles: true,
    temperature: true,
    items: true,
  })
  // Phase 2 (relations-v2): vehicles + items become optional on the
  // create-order API. The publish-order flow stamps `vehicles` from
  // the creator Org's `settings.vehicle_preferred_types[0]` (or the
  // picked Client's preferred vehicles, for business customers); when
  // even that is empty the backend resolver picks a fallback. `items`
  // is now optional too — many publish flows submit only a vehicle +
  // lane and let the carrier fill in load detail later. See
  // `OrderFetcher.resolveVehiclesForCreate` and `OrderFetcher.items`.
  .partial({ vehicles: true, items: true })
  .extend({
    /** the pickup & drop off location for items */
    destinations: z.array(destinationSchema).min(2),
    /** the date at which the user want to pick up items */
    pickupDate: OrderSchema.data.shape.dates.shape.pickup,
    /**
     * Expected delivery window (`dates.dropoffExpectation`). Optional —
     * when unset the backend estimates it from the route (500 km a day)
     * and the government waybill files its 5-day default
     * (`resolveBayanTripDates`); a stated haul length («خمس أيام») lands
     * here as pickup + N days.
     */
    deliveryDate: OrderSchema.data.shape.dates.shape.pickup.optional(),
    /**
     * Who chose `deliveryDate`. Defaults to `user` whenever a delivery
     * date is sent; pass `distance` when the caller only carried an
     * estimate forward (e.g. a moved loading day keeping its length).
     */
    deliveryDateSource: orderDropoffExpectationSourceSchema.optional(),
    /** the number of requested vehicles by the user */
    vehicleQuantity: loadSchema.shape.job.shape.vehicleQuantity.default(1),

    /** this will be the `Receiver` of items.
     *
     * In other words, this will be the the customer of our user app.
     */
    receiver: orderBeneficiarySchema
      .extend({
        contact: orderContactSchema.omit({ sameAs: true }),
      })
      .pick({
        _id: true,
        contact: true,
        name: true,
        customerType: true,
      })
      .optional()
      .nullable(),

    /**
     * CRM Client doc id (`Client._id`) that this order is being
     * created **for**. Set by the carrier-dashboard publish-order flow
     * after the user picks a client; admin-panel and customer-dashboard
     * leave it undefined.
     *
     * Phase 1 (Relations-v2) uses `clientId` for THREE downstream
     * effects, not just contract seeding:
     *
     *   1. **Pricing** — threaded through
     *      `OrderRouteService.getOrderRoutePrice` as the `buyerId`
     *      so `PricingResolutionService.findDirected(seller, buyer)`
     *      can quote against directed / agreement-bound Routes at
     *      create time, even before any leg is seeded.
     *   2. **Tenants** — the Client (and its linked Profile / Org,
     *      when signed) lands in `Order.tenants[]` via the buyer Party
     *      so the buyer sees the Order in their inbox from minute zero.
     *   3. **Contract seeding (gated)** — only when the creator is the
     *      seller AND `allocation.strategy === 'own_fleet'` (i.e. a
     *      carrier publishing their own fleet against a known client)
     *      do we seed `contracts[0]` with `buyer=client party`,
     *      `seller=creator party`, `status='accepted'`. In every other
     *      branch (auto / private_network / public_network) we leave
     *      `contracts: []` and let the existing Offer cascade
     *      (`escalateAutoCascade → Offer.acceptTarget →
     *      pushAcceptedContract`) push the first leg.
     *
     * Walk-in orders (no CRM client picked) leave this undefined.
     * See `docs/relations-v2/06-scenarios.md` (sub-sections A2 /
     * C-contracts / C3-contracts / E2).
     */
    clientId: objectIdSchema.optional(),

    /**
     * Phase 1 — explicit allocation strategy for the new Order.
     *
     * When omitted, the backend picks a default based on the creator's
     * role: `'own_fleet'` for carrier-side creators (publishing for an
     * own client), `'auto'` everywhere else (so the existing
     * `escalateAutoCascade` → Offer → `acceptTarget` cascade owns leg
     * creation). The carrier-dashboard publish flow stamps this from
     * the auth context; admin-panel and customer-dashboard leave it
     * unset.
     *
     * Pairs with `clientId` for the seeding gate in
     * `OrderTransformer.generateOrder`: only `own_fleet` + known
     * buyer Party seeds `contracts[0]`. See
     * `docs/relations-v2/06-scenarios.md` § A2 / § C-contracts.
     */
    allocation: orderAllocationSchema.optional(),

    /**
     * Optional **one-time price** for this Order, supplied by the caller.
     *
     * When provided, the backend pricing pipeline
     * (`OrderRouteService.getOrderRoutePrice` →
     * `PricingResolutionService`) is **bypassed**: the resolver does not
     * look up directed Routes, seller-own Routes, or buyer-own Routes for
     * this `(seller, buyer)` pair. The price is used as-is, persisted on
     * the seeded contract leg as
     * `pricingStrategy = 'one_time_pricing'`, and **no** Route snapshot
     * is attached (this number is a one-off, not promoted into the saved
     * Route book).
     *
     * Used by:
     *   - The reorder modal (`useOrderAgain`) when the source order's
     *     viewer leg is itself `one_time_pricing` or a positive
     *     `unresolved_pricing` — i.e. the user is replicating an
     *     off-Route deal and we don't want the resolver to silently
     *     re-quote it.
     *   - The edit-price drawer (when editing a draft before publish).
     *   - Any future flow where the UI needs to lock a manually-entered
     *     subtotal across the create boundary.
     *
     * Omit this field for the standard publish flow — the resolver will
     * still pick `route_directed_pricing` / `route_seller_own_pricing`
     * / `route_buyer_own_pricing` from the Route book, falling back to
     * `unresolved_pricing` (subtotal `0`) when nothing matches.
     *
     * See `docs/pricing/scenarios.md` § One-time prices and
     * `getPriceCategory` (`@hero/core`) for the UI mapping.
     */
    price: z
      .object({
        /**
         * Original (pre-discount) items subtotal in minor units of
         * `currency`. The same field is fed straight into
         * `AmountService` / `computeAmountPreview` on the backend, so
         * tax + total are derived from this number identically to the
         * frontend preview.
         */
        itemsOriginalSubtotal: z.number().min(0),
        /**
         * Currency for the supplied subtotal. Defaults to the Order's
         * currency (`SAR`) when omitted.
         */
        currency: currencySchema.optional(),
        /**
         * Whether to apply VAT on top. Defaults to `true` (Saudi VAT
         * 15%); pass `false` only for B2B exports or VAT-exempt loads.
         */
        taxable: z.boolean().optional(),
      })
      .optional(),

    /**
     * Optional POD copy preference for this order. When set, overrides
     * `Client.settings.pod_settings` / `Org.settings.pod_settings` at
     * create time and is stamped onto `Order.settings.podCopy`.
     */
    podSettings: orderPODCopySettingSchema.optional(),

    /** any special instructions entered by the customer */
    instructions: OrderSchema.data.shape.instructions,
  });

// create multi order
export const createMultiOrderAPISchema = z.object({
  routes: z.array(createOrderAPISchema).min(1),
});

/** this API is for creating new orders */
export type CreateOrderAPI = API<z.infer<typeof createOrderAPISchema>, Order[]>;

export type CreateMultiOrderAPI = API<z.infer<typeof createMultiOrderAPISchema>, Order[]>;

//* get all business orders
export const businessOrdersSchema = paginationSchema.extend(ordersQueryAPISchema.shape);
export type BusinessOrdersAPI = API<z.infer<typeof businessOrdersSchema>, Order[]>;

//* get all orders for current user
export const currentUserOrdersAPISchema = paginationSchema;
/**
 * get the orders for the current user.
 *
 * If the current user is:
 * - `business Admin/Employee`, it will return all business orders.
 * - `Individual Customer`, it will return all orders for the customer.
 * - `Freelancer Driver or Driver Company`, it will return orders related to the driver.
 * - `Carrier Admin/Employee`, it will return all orders related to the carrier company.
 *
 */
export type CurrentUserOrdersAPI = API<z.infer<typeof currentUserOrdersAPISchema>, Order[]>;

// * actions
export const orderOperationActionTypeSchema = z.enum(['add', 'delete', 'update']);
export type OrderOperationActionType = z.infer<typeof orderOperationActionTypeSchema>;
const orderBaseActionSchema = z.object({
  /** the type of operation you want to do */
  operation: orderOperationActionTypeSchema,
});
const orderActionSchema = orderBaseActionSchema.extend({
  /** the object id will be required if :
   *
   * - the thing you want to add/delete/update requires an _id such as carrier & driver.
   */
  _id: objectIdSchema,
});

//* image
const orderUpdateImageSchema = orderImageSchema.pick({ image: true, type: true, customRef: true });

// * amount
const orderAmountUpdateAPISchema = amountSchema
  .pick({
    itemsOriginalSubtotal: true,
  })
  .extend({
    taxable: z.boolean(),
    fees: z.array(feeSchema.pick({ type: true, amount: true })),
    /** the total paid amount */
    totalPaidAmount: priceSchema,
  })
  .partial({ totalPaidAmount: true, taxable: true });
export type OrderAmountUpdateAPI = z.infer<typeof orderAmountUpdateAPISchema>;

// * pod
export const orderUpdatePODAPISchema = z.object({
  /** the new status of the pod copy. */
  status: orderPODCopyStatusSchema.optional(),
  /** the owner of the POD. This is the person who is responsible for the POD at this stage.
   *
   * If empty, then we will use existing auth user as owner.
   *
   * The owner id must be super admin user (Hero Employee)
   */
  ownerId: objectIdSchema.optional(),
  /** the POD reference number. Can be set manually by admin users. */
  podRef: z.coerce.string().trim().optional(),
  /** the POD image status. Can be set manually by admin users to mark images as complete or not required. */
  podImageStatus: orderPODImageStatusSchema.optional(),
});

export const orderMessageAPISchema = z.object({
  /** the list of messages to send. */
  templateIds: z.array(orderAvailableMessagesSchema),
});

export type OrderMessageAPI = API<z.infer<typeof orderMessageAPISchema>, void>;
// * Order Update API
export const orderUpdateAPISchema = z.object({
  /** order id */
  _id: objectIdSchema,
  /** select the carrier update */
  receiver: orderBaseActionSchema
    .extend({ data: orderBeneficiarySchema.optional() })
    .optional()
    .nullable(),
  /** add/update/remove the assigned carrier */
  carrier: orderActionSchema.optional(),
  /** add/update/remove the assigned vehicle */
  vehicle: orderActionSchema.optional(),
  /** add/update/remove the assigned driver
   *
   * If you select a driver then we will automatically:
   * - Assign the vehicle associated with the chosen driver
   * - Assign the carrier which the driver belongs to (if any)
   * */
  driver: orderActionSchema.optional(),
  /** the action status you want to add such as book_order */
  action: OrderActionTypesSchema.optional(),
  /** the cancellation info of the order. this must be supplied when the action is order cancellation of any type (cancel_order, super_admin_canceled_order, etc). */
  cancellation: orderCancellationSchema.pick({ reason: true, internalNote: true }).optional(),
  /** the requested vehicle types by the customer*/
  vehicleTypes: createOrderAPISchema.shape.vehicles.optional(),

  /** items update */
  items: createOrderAPISchema.shape.items.optional(),

  /** update destinations */
  destinations: createOrderAPISchema.shape.destinations.optional(),

  /**
   * What to do with the price when a destination edit changes a city.
   *
   * Only consulted when the patch actually moves a stop to a different
   * city — a pin refinement inside the same city never touches the
   * price, so this is ignored there. Required for a cross-city edit so
   * the price change is always something the caller asked for rather
   * than a side effect they discover on the invoice.
   *
   * - `resolve_route` — re-quote the new lane and apply whatever the
   *   resolver returns (agreement price, then public rate).
   * - `keep_previous` — hold the current subtotal, mark the leg
   *   `manual`, and drop the now-stale Route snapshot.
   * - `custom` — apply `locationChangePrice` as the new load price,
   *   marking the leg `manual`. Fees, taxability, and insurance are
   *   preserved exactly as `resolve_route` preserves them, which is why
   *   this goes through the policy rather than a raw
   *   `beneficiaryAmount` write.
   *
   * An explicit `beneficiaryAmount` + `contractId` outranks all three.
   */
  locationChangePricing: z.enum(['resolve_route', 'keep_previous', 'custom']).optional(),

  /**
   * The load price to apply when `locationChangePricing` is `custom`.
   * Ignored otherwise.
   */
  locationChangePrice: priceSchema.optional(),

  pickupDate: createOrderAPISchema.shape.pickupDate.optional(),
  deliveryDate: createOrderAPISchema.shape.pickupDate.optional(),
  deliveryDateSource: createOrderAPISchema.shape.deliveryDateSource,

  /** updating carrier amount object ... every time you provide this, the amount object will be recalculated*/
  carrierAmount: orderAmountUpdateAPISchema.optional(),

  /** updating beneficiary / customer amount ... every time you provide this, the amount object will be recalculated*/
  beneficiaryAmount: orderAmountUpdateAPISchema.optional(),

  /**
   * which contract leg the price update targets.
   *
   * When set, the backend writes the new amount to that specific leg
   * after verifying the caller is a party (buyer OR seller) of it,
   * which is the correct authorization model for the network: any
   * party to a bilateral leg can edit the leg's price. When omitted,
   * the legacy two-column path runs (super-admin only) and writes
   * `beneficiaryAmount` to `contracts[0]` / `carrierAmount` to
   * `contracts[last]`. New drawers must always send `contractId`.
   */
  contractId: objectIdSchema.optional(),

  /**
   * how the buyer settles this order (`bank_transfer` / `cash`).
   *
   * Optional - omitting it leaves the existing per-leg
   * `contracts[i].payment.method` untouched. When provided, the
   * backend writes it to every contract leg's payment block in the
   * same patch as the price update so the buyer's chosen method
   * propagates through the chain.
   */
  paymentMethod: paymentMethodSchema.optional(),

  /** update the temperature type & degree */
  temperature: createOrderAPISchema.shape.temperature.optional(),
  instructions: createOrderAPISchema.shape.instructions.optional(),

  /**
   * Who hands the goods over — lands as the lead contact of the first pickup
   * stop, which is what the وثيقة files as the sender. Applied after any
   * `destinations` change in the same patch.
   */
  senderName: z.string().trim().min(1).optional(),
  /** Who receives the goods — lead contact of the last drop-off stop. */
  recipientName: z.string().trim().min(1).optional(),

  /** the order messages to send such as order confirmed, order cancelled, etc.
   * @deprecated Use POST /message/send (MessageSendAPI) instead.
   */
  messages: orderMessageAPISchema.optional(),

  /**
   * Reassign the order owner to an existing user. Backend rebuilds the
   * party snapshot + phone from that user. Omit to leave owner unchanged.
   */
  orderOwnerUserId: objectIdSchema
    .optional()
    .describe(
      'User id to set as orderOwner. Resolve from org members / search_users. Backend stamps name, party roles, and phone for messaging.',
    ),

  /**
   * Assign or change the CRM client on an existing order.
   *
   * Backend rebuilds `Order.client` + interim `sender`/`receiver`, and
   * keeps the commercial originator in sync: seeds `contracts[0]` when
   * the order has no legs yet, or rewrites only the head leg's `buyer`
   * when that buyer matches the previous CRM client. Mid-chain broker
   * buyers are never touched. Omit to leave the client unchanged.
   */
  clientId: objectIdSchema
    .optional()
    .describe(
      'CRM Client doc id to assign/change on the order. Resolve from search_clients / create_client.',
    ),

  /** update the POD copy status and owner. */
  pod: orderUpdatePODAPISchema.optional(),

  /** new images to add
   *
   * DO NOT include images that are already added in the order.
   *
   * This is only meant for new images
   */
  newImages: z.array(orderUpdateImageSchema).optional(),
  /** the location of the user making the request.
   *
   * for example, this cloud be the driver location when clicking arrived.
   */
  currentUserLocation: latLngSchema.optional(),

  /** a problem in the order to report.
   *
   * Important NOTE ... if you report a problem, you must add new image in `newImages` that verifies the problem
   */
  problem: OrderSchema.shape.problem.optional(),

  /** the review info of the order. This is used to verify a delivered order.
   *
   * When provided, the backend will automatically:
   * - Set the review.user from the authenticated user
   * - Set the review.verifiedDate to the current date
   */
  review: z
    .object({
      status: orderReviewStatusSchema,
    })
    .optional(),
});

export type OrderUpdateAPI = API<z.infer<typeof orderUpdateAPISchema>, Order>;

// * Order location-change preview API

/**
 * Why a cross-city destination edit is refused outright.
 *
 * - `pickup_city_locked` — pickup city change refused after the truck left
 *   pickup, because `CONFIG.order.locationChangeConfig.changePickupCity`
 *   is `'before_pickup'`.
 * - `dropoff_city_locked` — same for a dropoff stop when
 *   `changeDropoffCity` is `'before_pickup'`.
 * - `stop_count_changed` — the update path merges destinations by index,
 *   so adding or removing a stop misaligns ids, statuses, and times.
 *
 * When both knobs are `'anytime'` (the default), leaving pickup is not a
 * blocker — the edit goes through the normal price preview / policy path.
 *
 * An issued government waybill (وثيقة النقل) is deliberately absent. Bayan
 * has no route update, so Hero cancels the document and issues a replacement
 * once the change commits — see
 * `OrderWaybillLifecycleService.onPrintedFieldsChanged`.
 */
export const orderLocationChangeBlockedReasonSchema = z.enum([
  'pickup_city_locked',
  'dropoff_city_locked',
  'stop_count_changed',
]);
export type OrderLocationChangeBlockedReason = z.infer<
  typeof orderLocationChangeBlockedReasonSchema
>;

export const orderLocationChangePreviewAPISchema = z.object({
  /** the order being edited */
  _id: objectIdSchema,
  /**
   * Candidate destinations, identical in shape to what the subsequent
   * PATCH would send. The preview is a separate call rather than a PATCH
   * dry-run so the dashboard, mobile, and the agent all read the same
   * answer without risking a write.
   */
  destinations: createOrderAPISchema.shape.destinations,
  /** which contract leg's price the caller is asking about. */
  contractId: objectIdSchema.optional(),
});

/**
 * Everything a caller needs to explain a destination edit before doing it:
 * whether the city changed, whether that is allowed, what the price is
 * now, and what the new lane would quote.
 */
export type OrderLocationChangePreview = {
  crossCity: boolean;
  blockedReason?: OrderLocationChangeBlockedReason;
  /** per-stop breakdown of what moved, for multi-stop orders. */
  changes: {
    destinationId: string;
    type: z.infer<typeof orderDestinationTypeSchema>;
    previousCity?: string;
    newCity?: string;
    distanceKm: number;
  }[];
  /** the viewer leg's current load price. */
  previousPrice: number;
  previousPriceCategory: 'agreement' | 'everyone' | 'one_time' | 'unresolved';
  /** e.g. `"Riyadh → Jeddah"`. */
  previousRouteLabel: string;
  newRouteLabel: string;
  /** straight-line km between old and new pins, largest change wins. */
  distanceKm?: number;
  /** what the resolver quotes on the new lane, when it quotes anything. */
  resolvedPrice?: number;
  resolvedCategory?: 'agreement' | 'everyone';
  /** `false` when nothing is on file for the new lane. */
  hasRateOnNewRoute: boolean;
  /** what the backend will do absent an explicit `locationChangePricing`. */
  recommendedPolicy: 'resolve_route' | 'keep_previous';
  /** legs in a broker chain that keep their price untouched. */
  otherLegsUnchanged: number;
  currency: z.infer<typeof currencySchema>;
  /**
   * The viewer leg's applied fees, echoed so a caller can derive the
   * tax-inclusive total of a price the user is *typing* — the backend
   * can't compute a total for a number it hasn't been sent yet. Feed
   * these straight into `computeAmountPreview` from `@hero/core`; never
   * roll up fees by hand.
   */
  fees: Fee[];
  /**
   * The tax on the viewer leg, when it is taxable. `undefined` means the
   * leg carries no tax, so the load price *is* the total.
   */
  taxId?: TaxId;
};

export type OrderLocationChangePreviewAPI = API<
  z.infer<typeof orderLocationChangePreviewAPISchema>,
  OrderLocationChangePreview
>;

// * carriers
export const carrierOrdersSchema = paginationSchema.extend(ordersQueryAPISchema.shape);
/** use this API carrier orders such as delivered / canceled / in transit / etc orders */
export type CarrierOrdersAPI = API<z.infer<typeof carrierOrdersSchema>, Order[]>;

export const publishedOrdersSchema = paginationSchema.extend({
  vehicle: vehicleTypesSchema
    .optional()
    .describe('Filter published orders by requested vehicle type (single enum value).'),
  pickupId: objectIdSchema
    .optional()
    .describe(
      'Pickup city `Region._id`. Resolve from `search_regions` when the user names a city.',
    ),
  dropOffId: objectIdSchema
    .optional()
    .describe(
      'Drop-off city `Region._id`. Resolve from `search_regions` when the user names a city.',
    ),
});
/** use this API to get available loads/jobs in which carriers can book */
export type PublishedOrdersAPI = API<z.infer<typeof publishedOrdersSchema>, Order[]>;

//* get my orders (relations-v2 unified scope)
/**
 * Tenant-aware "my orders" query.
 *
 * The backend auto-scopes the result set to ANY order where the
 * caller is a tenant — backed by the multikey index on
 * `Order.tenants[]` (one seek regardless of role: customer / carrier /
 * broker / driver / individual). Callers do NOT need to pass their
 * `customerId` / `carrierId` — those filters are deliberately stripped
 * server-side (they duplicate tenant scope).
 *
 * Optional `clientId` AND-narrows to "my orders involving this CRM
 * Client" (used by the workspace orders tab and the client-drawer
 * orders tab). Matches the `client_id` tenant entry that
 * {@link TenantsBuilder.recompute} emits when an Order's
 * `user`/`buyer`/`seller` Party has `target='client'`.
 *
 * Optional `driverId` / `vehicleId` AND-narrow to orders assigned to
 * that Driver / Vehicle doc (`Order.driver._id` / `Order.vehicle._id`).
 * Used by the driver / vehicle drawer orders tabs.
 *
 * Carries the same shape as {@link OrdersQueryAPI} (status, dates,
 * vehicle, ref, etc.) so the unified UI can reuse the existing
 * filter inputs.
 */
/**
/**
 * Phase 3 role-context lens for `getMyOrders`.
 *
 * Picks which side of a contract leg the current user must be on
 * for the order to be returned. The tenant scope is enforced
 * independently, so a lens never widens access — it only narrows
 * within "orders I can see":
 *
 *   - `as_shipper`  — orders I published. Matches when any of my
 *     identity ids is on `order.shipper._id` or `order.shipper.ids[]`.
 *     Covers internal shipments AND orders I published on behalf of
 *     a CRM Client. Use this for the "Outbox / Published" tab.
 *   - `as_customer` — orders where my Party (any of my identity
 *     ids) is on a `contract.buyer` (or `order.shipper._id` for
 *     walk-in / pre-allocation orders).
 *   - `as_carrier`  — symmetric on `contract.seller` (or
 *     `order.carrier._id` once an executor has been allocated).
 *   - `as_driver`   — my profile is on `order.driver._id` /
 *     `order.driver.userId`.
 *   - `none`        — no lens, every Order I'm a tenant of.
 *
 * See `apps/backend/src/app/order/order-query.service.ts`
 * (`OrderLens`, `buildLensFilter`) for the predicate this
 * generates, and
 * `docs/relations-v2/03-multi-tenants-structure.md`
 * §"Role-context queries" for the design rationale.
 */
export const orderLensSchema = z.enum([
  'as_shipper',
  'as_customer',
  'as_carrier',
  'as_driver',
  'none',
]);
export type OrderLens = z.infer<typeof orderLensSchema>;

export const myOrdersAPISchema = paginationSchema
  .extend(ordersQueryAPISchema.shape)
  .extend({
    clientId: objectIdSchema
      .optional()
      .describe(
        'Filter by CRM Client doc id (`Client._id`). Matches the `client_id` tenant entry on the Order. Resolve from `search_clients` when the user names a client.',
      ),
    lens: orderLensSchema
      .optional()
      .describe(
        "Role-context lens: 'as_shipper' (I published) | 'as_customer' (I'm the buyer) | 'as_carrier' (I'm the seller) | 'as_driver' (I'm the driver) | 'none' (no narrow). Defaults server-side to 'none'.",
      ),
  });
export type MyOrdersAPI = API<z.infer<typeof myOrdersAPISchema>, Order[]>;

/** Waybill */
export const OrderWaybillAPISchema = z.object({ id: objectIdSchema });

/** if undefined, then it means that the waybill has not yet being generated */
export type OrderGetWaybillAPI = API<
  z.infer<typeof OrderWaybillAPISchema>,
  Order['waybill'] | undefined
>;
export type OrderGenerateWaybillAPI = API<z.infer<typeof OrderWaybillAPISchema>, Order['waybill']>;

const queryBooleanSchema = z.preprocess(
  v => (v === 'true' ? true : v === 'false' ? false : v),
  z.boolean(),
);

/** HTML waybill preview — always uses shared dummy order + org branding. */
export const orderWaybillHtmlPreviewAPISchema = z.object({
  locale: z.enum(['en', 'ar']).optional(),
  orgId: objectIdSchema.optional(),
  accent_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .optional(),
  use_org_logo: queryBooleanSchema.optional(),
  logo_shape: z.enum(['square', 'rectangle']).optional(),
  logo_background: z.enum(['white', 'none']).optional(),
  logo_size: z.enum(['small', 'medium', 'large']).optional(),
  logo_rounding: z.enum(['none', 'sm', 'md', 'full']).optional(),
});
export type OrderWaybillHtmlPreviewAPI = API<
  z.infer<typeof orderWaybillHtmlPreviewAPISchema>,
  string
>;

/** Dev/QA — generate HTML-engine PDF for a real order. */
export type OrderWaybillHtmlGenerateAPI = API<
  z.infer<typeof OrderWaybillAPISchema>,
  Order['waybill']
>;

//* Vehicle
export const orderBestMatchCarrierVehiclesAPISchema = paginationSchema.extend({
  orderId: objectIdSchema.describe(
    "The order id to find best-match carrier vehicles for. Backend ranks vehicles by match on `classification.type` against the order's requested vehicle type, plus availability.",
  ),
});
export type OrderBestMatchCarrierVehiclesAPI = API<
  z.infer<typeof orderBestMatchCarrierVehiclesAPISchema>,
  Vehicle[]
>;

/** This API returns the Active order for the driver. */
export type ActiveDriverOrderAPI = API<undefined, Order | undefined>;

// ==== Admin Apis ====
export const createOrderAPISchemaAdmin = createOrderAPISchema
  .extend(orderUpdateAPISchema.pick({ carrierAmount: true, beneficiaryAmount: true }).shape)
  .extend({
    /** the customer org id or user id if individual customer*/
    customerId: objectIdSchema,
    /** the member user id. If individual customer, then this will be same as customerId. */
    memberUserId: objectIdSchema,
  });
export type CreateOrderAPIAdmin = API<z.infer<typeof createOrderAPISchemaAdmin>, Order>;

export const createMultiOrderAPISchemaAdmin = z.object({
  /** the customer org id or user id if individual customer*/
  routes: z.array(createOrderAPISchemaAdmin),
});
export type CreateMultiOrderAPIAdmin = API<z.infer<typeof createMultiOrderAPISchemaAdmin>, Order[]>;

export const orderAdminBulkUpdateAPISchema = z.object({
  /** the list of orders to run the updates for */
  orders: z.array(orderUpdateAPISchema),
});

export type OrderAdminBulkUpdateAPI = API<
  z.infer<typeof orderAdminBulkUpdateAPISchema>,
  {
    /** the list of orders that were updated successfully */
    orders: Order[];
    /** the number of orders that failed to update */
    failedOrdersCounts: number;
  }
>;
export const ordersGetAllAdminAPISchema = paginationSchema
  .extend(ordersQueryAPISchema.shape)
  .extend({
    orgId: objectIdSchema
      .optional()
      .describe(
        'CRM / admin org scope: include only orders where this org appears in `Order.tenants[]`. Pair with `lens` to narrow by commercial role (seller, buyer, shipper).',
      ),
    lens: orderLensSchema
      .optional()
      .describe(
        "Role-context lens when `orgId` is set: 'none' (all tenant orders) | 'as_carrier' (seller) | 'as_customer' (buyer) | 'as_shipper' (publisher). Defaults to 'none'.",
      ),
  });
export type OrdersGetAllAdminAPI = API<z.infer<typeof ordersGetAllAdminAPISchema>, Order[]>;

// Zod enum for column names - single source of truth
export const orderCSVColumnNameSchema = z.enum([
  'order_ref',
  'order_pickup_date',
  'order_delivery_date',
  'order_status',
  // Legacy broker-era names — kept for backward compat. Mapped on
  // render to the first contract leg's buyer (customer) or last leg's
  // seller (carrier).
  'customer_name',
  'customer_contact_name',
  'customer_contact_phone',
  'customer_subtotal',
  'customer_total',
  'customer_tax',
  'customer_fees',
  'customer_shipping',
  'customer_fees_notes',
  // New bilateral names — buyer = first-leg buyer (originating
  // customer), seller = last-leg seller (executing carrier).
  'buyer_name',
  'buyer_contact_name',
  'buyer_contact_phone',
  'buyer_subtotal',
  'buyer_total',
  'buyer_tax',
  'buyer_fees',
  'buyer_shipping',
  'buyer_fees_notes',
  // Viewer-leg neutral columns — populated from the requesting org's
  // contract leg via `getViewerLeg`. Same header names regardless of
  // whether the viewer is the buyer or seller on that leg.
  'counterparty_name',
  'counterparty_contact_name',
  'counterparty_contact_phone',
  'subtotal',
  'total',
  'tax',
  'fees',
  'shipping',
  'fees_notes',
  'driver_name',
  'driver_phone',
  'driver_national_id',
  'driver_country',
  // Legacy carrier_* columns — same backward-compat shim.
  'carrier_name',
  'carrier_subtotal',
  'carrier_total',
  'carrier_tax',
  'carrier_fees',
  'carrier_fees_notes',
  'carrier_shipping',
  // New seller_* counterparts.
  'seller_name',
  'seller_subtotal',
  'seller_total',
  'seller_tax',
  'seller_fees',
  'seller_fees_notes',
  'seller_shipping',
  'vehicle_type',
  'vehicle_plate',
  'load_item_name',
  'load_weight',
  'load_category',
  'load_quantity',
  'load_packing',
  'load_items_cost',
  'pickup_city',
  'pickup_beneficiary',
  'pickup_beneficiary_type',
  'pickup_contact_name',
  'pickup_contact_phone',
  'pickup_instructions',
  'pickup_saved_place_name',
  'dropoff_city',
  'dropoff_beneficiary',
  'dropoff_beneficiary_type',
  'dropoff_contact_name',
  'dropoff_contact_phone',
  'dropoff_instructions',
  'dropoff_saved_place_name',
  'pod_status',
  'pod_ref',
  'images',
  // Full chain serialized as JSON for orders with >2 legs. Each
  // entry is `{ contractId, buyer, seller, amount }` so downstream
  // tools can reconstruct intermediate brokers / sub-carriers.
  'chain_legs',
  // Legacy broker-era commission columns — parked.
  'commission_total',
  'commission_subtotal',
  'commission_tax',
  'commission_fees',
  'commission_shipping',
  'verification_status',
]);

// Column names as a union type derived from the enum
export type OrderCSVColumnName = z.infer<typeof orderCSVColumnNameSchema>;

// Export Orders API
export const orderExportAdminAPISchema = ordersQueryAPISchema.extend({
  // No pagination needed - exports all matching orders
  /** Optional array of column names to include in the export. If not provided, all columns will be exported. */
  columns: z.array(orderCSVColumnNameSchema).optional(),
});
export type OrderExportAdminAPI = API<z.infer<typeof orderExportAdminAPISchema>, string>;

// Customer Export Orders API
export const orderExportCustomerAPISchema = ordersQueryAPISchema.extend({
  // No pagination needed - exports all matching orders
  /** Optional array of column names to include in the export. If not provided, party columns will be exported. */
  columns: z.array(orderCSVColumnNameSchema).optional(),
});
export type OrderExportCustomerAPI = API<z.infer<typeof orderExportCustomerAPISchema>, string>;

// Carrier Export Orders API
export const orderExportCarrierAPISchema = ordersQueryAPISchema.extend({
  // No pagination needed - exports all matching orders
  /** Optional array of column names to include in the export. If not provided, party execution columns will be exported. */
  columns: z.array(orderCSVColumnNameSchema).optional(),
});
export type OrderExportCarrierAPI = API<z.infer<typeof orderExportCarrierAPISchema>, string>;

// Grouped Orders API
export const orderGroupBySchema = z.enum(['city', 'route', 'vehicle', 'carrier', 'customer']);
export type OrderGroupBy = z.infer<typeof orderGroupBySchema>;

/**
 * Vehicle type breakdown showing count of each vehicle type
 */
export const vehiclesBreakdownSchema = z.object({
  /** Vehicle type (e.g., 'truck_flatbed', 'truck_curtain_side') */
  type: z.string(),
  /** Number of orders using this vehicle type */
  count: z.number(),
});
export type VehiclesBreakdown = z.infer<typeof vehiclesBreakdownSchema>;

/**
 * Aggregated route information within a group
 */
export const routeAggregatedSchema = z.object({
  /** Route ID */
  _id: idSchema,
  /** Route name */
  name: z.string(),
  /** Number of orders for this route */
  ordersCount: z.number(),
  /** Breakdown of vehicle types used in orders for this route */
  vehiclesBreakdown: z.array(vehiclesBreakdownSchema),
});
export type RouteAggregated = z.infer<typeof routeAggregatedSchema>;

/**
 * Aggregated customer information within a group
 */
export const customerAggregatedSchema = z.object({
  /** Customer ID */
  _id: idSchema,
  /** Customer name */
  name: z.string(),
  /** Number of orders for this customer */
  ordersCount: z.number(),
  /** Breakdown of vehicle types used in orders for this customer */
  vehiclesBreakdown: z.array(vehiclesBreakdownSchema),
});
export type CustomerAggregated = z.infer<typeof customerAggregatedSchema>;

/**
 * Aggregated carrier information within a group
 */
export const carrierAggregatedSchema = z.object({
  /** Carrier ID */
  _id: idSchema,
  /** Carrier name */
  name: z.string(),
  /** Number of orders for this carrier */
  ordersCount: z.number(),
  /** Breakdown of vehicle types used in orders for this carrier */
  vehiclesBreakdown: z.array(vehiclesBreakdownSchema),
});
export type CarrierAggregated = z.infer<typeof carrierAggregatedSchema>;

/**
 * Summary of routes within a group
 */
export const routesSummarySchema = z.object({
  /** Total number of unique routes */
  count: z.number(),
  /** List of aggregated route information */
  list: z.array(routeAggregatedSchema),
});
export type RoutesSummary = z.infer<typeof routesSummarySchema>;

/**
 * Summary of customers within a group
 */
export const customersSummarySchema = z.object({
  /** Total number of unique customers */
  count: z.number(),
  /** List of aggregated customer information */
  list: z.array(customerAggregatedSchema),
});
export type CustomersSummary = z.infer<typeof customersSummarySchema>;

/**
 * Summary of carriers within a group
 */
export const carriersSummarySchema = z.object({
  /** Total number of unique carriers */
  count: z.number(),
  /** List of aggregated carrier information */
  list: z.array(carrierAggregatedSchema),
});
export type CarriersSummary = z.infer<typeof carriersSummarySchema>;

/**
 * Summary information for an order group
 * Note: totalOrderCount is now at the group level (as totalCount), not in the summary
 */
export const orderGroupSummarySchema = z.object({
  /** Total customer price for all orders in this group */
  totalCustomerPrice: z.number(),
  /** Total carrier price for all orders in this group */
  totalCarrierPrice: z.number(),
  /** Currency code (e.g., 'SAR') */
  currency: currencySchema,
  /** Aggregated routes information */
  routes: routesSummarySchema,
  /** Aggregated customers information */
  customers: customersSummarySchema,
  /** Aggregated carriers information */
  carriers: carriersSummarySchema,
});
export type OrderGroupSummary = z.infer<typeof orderGroupSummarySchema>;

export type OrderGroup = Group<Order, OrderGroupSummary>;

export const ordersGetAllGroupedAdminAPISchema = paginationSchema
  .extend(ordersQueryAPISchema.shape)
  .extend({
    groupBy: orderGroupBySchema,
  });
export type OrdersGetAllGroupedAdminAPI = API<
  z.infer<typeof ordersGetAllGroupedAdminAPISchema>,
  OrderGroup[]
>;

// Get more orders for a specific group
export const ordersGetGroupOrdersAdminAPISchema = paginationSchema
  .extend(ordersQueryAPISchema.shape)
  .extend({
    groupBy: orderGroupBySchema,
    groupKey: z.string(), // The group key to fetch orders for
  });
export type OrdersGetGroupOrdersAdminAPI = API<
  z.infer<typeof ordersGetGroupOrdersAdminAPISchema>,
  Order[]
>;

// Common Routes API
export const getCommonRoutesAPISchema = z.object({
  /** the customer id to get common routes for */
  customerId: objectIdSchema,
});

export const commonRouteResponseSchema = z.object({
  /** the destinations array (pickup and dropoff) */
  destinations: createOrderAPISchema.shape.destinations,
  /** the vehicle type */
  vehicle: vehicleTypesSchema,
  /** the customer price (average from historical orders) */
  customerPrice: priceSchema,
  /** the carrier price (average from historical orders) */
  carrierPrice: priceSchema,
  /** the frequency count (how many times this route was used) */
  frequency: z.number(),
});

export type GetCommonRoutesAPI = API<
  z.infer<typeof getCommonRoutesAPISchema>,
  z.infer<typeof commonRouteResponseSchema>[]
>;

// ─────────────────────────────────────────────────────────────────────────────
// New order-contract-offer surfaces.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manually escalate a stalled own-fleet order to a private_network broadcast.
 *
 * Used when the originator (or an admin) hits "Escalate to network" because
 * the order has been sitting on own_fleet past `Order.allocation.autoEscalateAfter`.
 * Server-side this delegates to `OrderService.escalateToNetwork(orderId)`
 * which in turn calls `OfferService.create({visibility: 'private_network'})`
 * with the originator's network as the default target list.
 */
export const orderEscalateAPISchema = z.object({
  orderId: objectIdSchema.describe('Order id to escalate from own-fleet to a network broadcast.'),
  promoteToPublic: z
    .boolean()
    .optional()
    .describe(
      "When true and the caller IS `Order.shipper`, broadcast goes to `visibility: 'public_load_board'`. Otherwise the server forces `'private_network'` (public-promotion rule).",
    ),
});
export type OrderEscalateAPI = API<z.infer<typeof orderEscalateAPISchema>, Order>;

/**
 * Force a counter-state on an already-pushed contract leg. Rare \u2014 most
 * counters live on the Offer; this exists for admin overrides where a
 * contract was pushed and a later renegotiation is needed.
 */
export const orderContractCounterAPISchema = z.object({
  orderId: objectIdSchema,
  contractId: objectIdSchema,
  note: z.string().optional(),
});
export type OrderContractCounterAPI = API<
  z.infer<typeof orderContractCounterAPISchema>,
  Order
>;

/** Cancel an already-pushed contract leg. */
export const orderContractCancelAPISchema = z.object({
  orderId: objectIdSchema,
  contractId: objectIdSchema,
  note: z.string().optional(),
});
export type OrderContractCancelAPI = API<
  z.infer<typeof orderContractCancelAPISchema>,
  Order
>;

/** Mark a contract leg completed (typically on delivery + settlement). */
export const orderContractCompleteAPISchema = z.object({
  orderId: objectIdSchema,
  contractId: objectIdSchema,
  note: z.string().optional(),
});
export type OrderContractCompleteAPI = API<
  z.infer<typeof orderContractCompleteAPISchema>,
  Order
>;

/**
 * Lean public order tracking DTO — no prices, no party IDs, no contacts,
 * no full street addresses. Safe to render on an unauthenticated share
 * link (`tryhero.io/o/[ref]`).
 */
export const publicOrderDestinationSchema = z.object({
  type: orderDestinationTypeSchema.describe("Stop kind: 'pickup' or 'dropoff'."),
  status: orderDestinationStatusSchema.describe('Per-stop journey status.'),
  city: z
    .string()
    .describe('Display city / region name only — never a full street address.'),
  label: z
    .string()
    .optional()
    .describe('Optional saved-place nickname when the shipper named the stop.'),
});

export const publicOrderSchema = z.object({
  ref: z.string().describe('Order main ref (shareable code, e.g. ACD15171258).'),
  status: orderOverallStatusSchema.describe('Overall order journey status.'),
  destinations: z
    .array(publicOrderDestinationSchema)
    .min(2)
    .describe('Ordered stops — pickup first, dropoff last, intermediates in between.'),
  shipperName: z
    .string()
    .describe(
      'Publisher / shipper display name — shown as the page brand so the share link feels like their system.',
    ),
  carrierName: z
    .string()
    .optional()
    .describe('Assigned carrier display name when a carrier is on the order.'),
  driverName: z
    .string()
    .optional()
    .describe('Assigned driver first name / initials only — never phone.'),
  driverPhone: z
    .string()
    .optional()
    .describe(
      'Assigned driver sign-in phone — only when `surface=driver` is requested (driver assignment deep links).',
    ),
  vehicleType: vehicleTypesSchema
    .optional()
    .describe(
      'Requested or assigned vehicle type enum key (e.g. `truck_flatbed`) for localized labels via `m-vehicle:types.*`.',
    ),
  vehiclePlate: z
    .string()
    .optional()
    .describe(
      'Raw assigned vehicle plate string — format with `getVehiclePlateNumber().displayAll` for UI.',
    ),
  waybillUrl: z
    .string()
    .url()
    .optional()
    .describe('Public URL of the waybill PDF/image when ready — open in a new tab.'),
  pickupAt: dateSchema
    .optional()
    .describe('Scheduled pickup window end (`dates.pickup.to`) as ISO date.'),
  etaAt: dateSchema
    .optional()
    .describe(
      'Expected dropoff window end (`dates.dropoffExpectation.to`) when known.',
    ),
  deliveredAt: dateSchema
    .optional()
    .describe('Delivery timestamp when the order is delivered.'),
  updatedAt: dateSchema.describe('Order document `updatedAt` for freshness UI.'),
  loadSummary: z
    .string()
    .optional()
    .describe(
      'Human-readable load hint for driver share links (e.g. vehicle + weight). Safe for public pages.',
    ),
  pickupMapsUrl: z
    .string()
    .url()
    .optional()
    .describe(
      'Google Maps search link for the pickup coordinates when an accurate pin exists.',
    ),
});
export type PublicOrder = z.infer<typeof publicOrderSchema>;

/**
 * Unauthenticated public order lookup — by shareable ref or Mongo id.
 * Backend is `@Public()` + per-IP throttled. Response is {@link PublicOrder}
 * (never the full Order document).
 */
export const orderGetPublicAPISchema = z
  .object({
    ref: z
      .string()
      .trim()
      .min(4)
      .max(32)
      .regex(/^[A-Za-z0-9]+$/)
      .optional()
      .describe(
        'Order `ref.main` (preferred) or `ref.short`. Alphanumeric share code from WhatsApp / SMS links.',
      ),
    id: objectIdSchema
      .optional()
      .describe('Order `_id` — alternative lookup when the caller has the ObjectId.'),
    surface: z
      .enum(['driver'])
      .optional()
      .describe(
        'When `driver`, the response may include `driverPhone` for carrier-app login prefill on `/d/` links.',
      ),
  })
  .refine(data => !!(data.ref || data.id), { message: 'ref_or_id_required' });
export type OrderGetPublicAPI = API<
  z.infer<typeof orderGetPublicAPISchema>,
  PublicOrder
>;
